import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, ScanBarcode, CheckCircle, Package, Box, Plus, X, Users, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { pjoSplitText } from '@/components/processingJo/PjoOutcomePick';

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

const AssortmentPacking = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  // step: 'staff' | 'scanning' | 'bundle' | 'result'
  const [step, setStep] = useState('staff');

  // Multi-staff state
  const [staffValue, setStaffValue] = useState('');
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [validating, setValidating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [sessionId, setSessionId] = useState('');

  const [usnValue, setUsnValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);

  const [bundleValue, setBundleValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Admin-only: edit wrongly-tagged bundle code
  const currentUser = getUser();
  const isAdmin = currentUser?.role === 'Admin';
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapNewCode, setSwapNewCode] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [swapLoading, setSwapLoading] = useState(false);

  const handleSwapBundleCode = async () => {
    if (!result?.bundle_code) return;
    const nc = swapNewCode.trim();
    if (!nc) { toast.error('New bundle code required'); return; }
    if (!window.confirm(
      `Replace ${result.bundle_code} with ${nc}?\n\nThe old code will be PERMANENTLY RETIRED and cannot be reused.`
    )) return;
    setSwapLoading(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/admin/swap-bundle-code', {
        old_bundle_code: result.bundle_code,
        new_bundle_code: nc,
        reason: swapReason.trim(),
      });
      toast.success(`Bundle code updated to ${resp.data.new_bundle_code}`);
      setResult({ ...result, bundle_code: resp.data.new_bundle_code });
      setSwapOpen(false);
      setSwapNewCode('');
      setSwapReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to swap bundle code');
    } finally {
      setSwapLoading(false);
    }
  };

  // History tab (admin-only): list past packing sessions and allow per-row
  // bundle-code corrections. Reuses the existing /admin/swap-bundle-code endpoint.
  const [activeTab, setActiveTab] = useState('pack'); // 'pack' | 'history'
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEditRow, setHistoryEditRow] = useState(null);
  const [historyNewCode, setHistoryNewCode] = useState('');
  const [historyReason, setHistoryReason] = useState('');
  const [historyEditLoading, setHistoryEditLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page: String(historyPage), limit: '20' });
      if (historySearch.trim()) params.append('search', historySearch.trim());
      const res = await apiClient.get(`/assortment-ops/packing/history?${params}`);
      setHistory(res.data.sessions || []);
      setHistoryTotal(res.data.total || 0);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historySearch]);

  React.useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  const openHistoryEdit = (row) => {
    setHistoryEditRow(row);
    setHistoryNewCode('');
    setHistoryReason('');
  };

  const submitHistoryEdit = async () => {
    if (!historyEditRow) return;
    const nc = historyNewCode.trim();
    if (!nc) { toast.error('New bundle code required'); return; }
    if (nc === historyEditRow.bundle_code) { toast.info('Same code — nothing to change'); return; }
    if (!window.confirm(
      `Replace ${historyEditRow.bundle_code} with ${nc}?\n\n` +
      `The old code will be PERMANENTLY RETIRED and cannot be reused.`
    )) return;
    setHistoryEditLoading(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/admin/swap-bundle-code', {
        old_bundle_code: historyEditRow.bundle_code,
        new_bundle_code: nc,
        reason: historyReason.trim(),
      });
      toast.success(`Bundle code updated → ${resp.data.new_bundle_code} (${resp.data.usn_count} USNs cascaded)`);
      setHistoryEditRow(null);
      setHistoryNewCode('');
      setHistoryReason('');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to swap bundle code');
    } finally {
      setHistoryEditLoading(false);
    }
  };

  const focusUsn = useCallback(() => setTimeout(() => usnInputRef.current?.focus(), 80), []);
  const focusStaff = useCallback(() => setTimeout(() => staffInputRef.current?.focus(), 80), []);

  // Add one staff by validating with backend
  const handleAddStaff = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.error('Staff already added');
      setStaffValue('');
      focusStaff();
      return;
    }
    setValidating(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/validate-staff', { staff_id: id });
      setStaffList((prev) => [...prev, { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name }]);
      setStaffValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      focusStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid staff ID');
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveStaff = (staff_id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== staff_id));
  };

  const handleStartSession = async () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStarting(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/start-session', {
        staff_ids: staffList.map((s) => s.staff_id),
      });
      setSessionId(resp.data.session_id);
      toast.success(`Session ${resp.data.session_id} started`);
      setStep('scanning');
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start session');
    } finally {
      setStarting(false);
    }
  };

  const handleUsnScan = async (e) => {
    e.preventDefault();
    const code = usnValue.trim();
    if (!code || scanning) return;
    if (scannedItems.some((i) => i.usn_code === code)) {
      toast.error('Already scanned');
      setUsnValue('');
      return;
    }
    setScanning(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/scan-usn', { usn_code: code, session_id: sessionId });
      setScannedItems((prev) => [
        {
          usn_code: resp.data.usn_code,
          bundle_code: resp.data.bundle_code,
          design_name: resp.data.design_name,
          pjo_id: resp.data.pjo_id || '',
          is_rework: resp.data.is_rework === true,
          rework_source_bundle: resp.data.rework_source_bundle,
          rework_source_section: resp.data.rework_source_section,
        },
        ...prev,
      ]);
      if (resp.data.is_rework) {
        toast.success(
          `Rework USN — from ${resp.data.rework_source_bundle} (${resp.data.rework_source_section || 'source'})`,
          { duration: 1500 }
        );
      }
      setUsnValue('');
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
      setUsnValue('');
      focusUsn();
    } finally {
      setScanning(false);
    }
  };

  const handleRemoveUsn = async (code) => {
    try {
      await apiClient.post('/assortment-ops/packing/remove-usn', { usn_code: code, session_id: sessionId });
      setScannedItems((prev) => prev.filter((i) => i.usn_code !== code));
      toast.success(`Removed ${code}`);
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove USN');
    }
  };

  const handleProceedToBundle = () => {
    if (scannedItems.length === 0) {
      toast.error('Scan USNs first');
      return;
    }
    setStep('bundle');
    setTimeout(() => bundleInputRef.current?.focus(), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const bc = bundleValue.trim();
    if (!bc) {
      toast.error('Scan bundle barcode');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await apiClient.post('/assortment-ops/packing/submit', { session_id: sessionId, bundle_code: bc });
      toast.success(resp.data.message);
      setResult(resp.data);
      setStep('result');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep('staff');
    setStaffList([]);
    setStaffValue('');
    setSessionId('');
    setScannedItems([]);
    setBundleValue('');
    setResult(null);
  };

  // Live qty split preview
  const total = scannedItems.length;
  const splitPreview = staffList.length > 0
    ? staffList.map((s, i) => {
        const base = Math.floor(total / staffList.length);
        const remainder = total - base * staffList.length;
        return { ...s, qty: base + (i < remainder ? 1 : 0) };
      })
    : [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="assortment-packing-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Assortment Packing</h1>
          <p className="text-xs text-gray-500">Pack USNs into bundles (multi-staff supported)</p>
        </div>
        {staffList.length > 0 && step !== 'staff' && (
          <div
            className="hidden sm:flex items-center gap-2 bg-cyan-50 px-3 py-1.5 rounded-lg"
            data-testid="staff-badge"
          >
            <Users className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-medium text-cyan-800">{staffList.length} staff</span>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Admin tab bar (Pack | History) */}
        {isAdmin && (
          <div className="flex gap-1 bg-white border rounded-lg p-1 w-fit" data-testid="admin-tabs">
            <button
              onClick={() => setActiveTab('pack')}
              className={`px-4 py-1.5 text-sm rounded-md transition ${
                activeTab === 'pack' ? 'bg-cyan-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-pack"
            >
              Pack
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-1.5 text-sm rounded-md transition ${
                activeTab === 'history' ? 'bg-cyan-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-history"
            >
              History (Admin)
            </button>
          </div>
        )}

        {(!isAdmin || activeTab === 'pack') && (
          <>
        {step === 'staff' && (
          <Card className="border-cyan-200">
            <CardHeader className="text-center pb-2">
              <Box className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
              <CardTitle className="text-base">Add Staff Working on This Session</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Scan one or more staff IDs. USN qty will be split equally when you submit.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={handleAddStaff} className="flex gap-2">
                <Input
                  ref={staffInputRef}
                  value={staffValue}
                  onChange={(e) => setStaffValue(e.target.value)}
                  placeholder="Scan or type Staff ID"
                  autoFocus
                  data-testid="staff-input"
                />
                <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-add-btn">
                  {validating ? '...' : (<><Plus className="w-4 h-4 mr-1" />Add</>)}
                </Button>
              </form>

              {staffList.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 font-medium">{staffList.length} staff added:</p>
                  {staffList.map((s) => (
                    <div
                      key={s.staff_id}
                      className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                      data-testid={`staff-chip-${s.staff_id}`}
                    >
                      <UserCheck className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800 text-sm flex-1">{s.staff_name}</span>
                      <span className="text-xs text-gray-500">{s.staff_id}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveStaff(s.staff_id)}
                        data-testid={`staff-remove-${s.staff_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                size="lg"
                disabled={staffList.length === 0 || starting}
                onClick={handleStartSession}
                data-testid="start-session-btn"
              >
                {starting ? 'Starting…' : `Start Session (${staffList.length} staff)`}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 'scanning' && (
          <>
            <div className="flex items-center justify-between bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-sm">
              <span className="font-mono text-cyan-700">{sessionId}</span>
              <Badge className="bg-cyan-100 text-cyan-800">{scannedItems.length} USNs</Badge>
            </div>
            <Card>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Scan USN Codes (PRESSED / FOLDED)
                </label>
                <form onSubmit={handleUsnScan} className="flex gap-2">
                  <Input
                    ref={usnInputRef}
                    value={usnValue}
                    onChange={(e) => setUsnValue(e.target.value)}
                    placeholder="Scan USN..."
                    autoFocus
                    data-testid="usn-scan-input"
                  />
                  <Button type="submit" disabled={scanning || !usnValue.trim()}>
                    {scanning ? '...' : <ScanBarcode className="w-4 h-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Live qty split preview */}
            {staffList.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" /> Qty split preview (total: {total})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className={`grid gap-2 ${
                      splitPreview.length === 1 ? 'grid-cols-1' :
                      splitPreview.length === 2 ? 'grid-cols-2' :
                      splitPreview.length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'
                    }`}
                  >
                    {splitPreview.map((s, i) => (
                      <div
                        key={s.staff_id}
                        className={`rounded-lg p-2 text-center ${
                          ['bg-blue-50', 'bg-purple-50', 'bg-amber-50', 'bg-emerald-50', 'bg-rose-50'][i % 5]
                        }`}
                        data-testid={`staff-qty-${s.staff_id}`}
                      >
                        <p className="text-xs text-gray-500 truncate">{s.staff_name}</p>
                        <p className="font-bold text-gray-800">{s.qty} pcs</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {scannedItems.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    Scanned USNs ({scannedItems.length})
                    {scannedItems.some(i => i.is_rework) && (
                      <span className="ml-2 text-xs font-normal text-amber-700">
                        — {scannedItems.filter(i => i.is_rework).length} from Rework Pool
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {scannedItems.map((item) => (
                      <div
                        key={item.usn_code}
                        className={`flex items-center gap-2 rounded px-3 py-1.5 text-sm ${
                          item.is_rework ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
                        }`}
                        data-testid={`scanned-usn-${item.usn_code}`}
                      >
                        <CheckCircle className={`w-3.5 h-3.5 ${item.is_rework ? 'text-amber-600' : 'text-green-500'}`} />
                        <span className="font-mono">{item.usn_code}</span>
                        <span className="text-xs text-gray-400">{item.design_name}</span>
                        {item.pjo_id && (
                          <Badge className="h-5 px-1.5 bg-slate-100 text-slate-700 border border-slate-300 text-[10px] font-mono" data-testid={`scanned-pjo-${item.usn_code}`}>
                            {item.pjo_id}
                          </Badge>
                        )}
                        {item.is_rework && (
                          <Badge className="ml-auto h-5 px-1.5 bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">
                            REWORK · {item.rework_source_bundle}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 ${item.is_rework ? '' : 'ml-auto'}`}
                          onClick={() => handleRemoveUsn(item.usn_code)}
                          title="Remove this USN (wrongly scanned)"
                          data-testid={`remove-usn-${item.usn_code}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            <Button
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              size="lg"
              onClick={handleProceedToBundle}
              disabled={scannedItems.length === 0}
              data-testid="proceed-bundle-btn"
            >
              <Package className="w-4 h-4 mr-2" /> Proceed to Scan Bundle Label
            </Button>
          </>
        )}

        {step === 'bundle' && (
          <>
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-sm text-center">
              <span className="font-bold">{scannedItems.length} USNs</span> ready to pack across{' '}
              <span className="font-bold">{staffList.length} staff</span>
            </div>
            <Card className="border-cyan-200">
              <CardHeader className="text-center pb-2">
                <Package className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
                <CardTitle className="text-base">Scan Bundle Barcode Label</CardTitle>
                <p className="text-sm text-gray-500">Scan the pre-printed bundle label to assign USNs</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input
                    ref={bundleInputRef}
                    value={bundleValue}
                    onChange={(e) => setBundleValue(e.target.value)}
                    placeholder="Scan bundle barcode..."
                    autoFocus
                    data-testid="bundle-input"
                  />
                  <Button
                    className="w-full"
                    type="submit"
                    size="lg"
                    disabled={submitting || !bundleValue.trim()}
                    data-testid="submit-packing-btn"
                  >
                    {submitting ? 'Creating...' : `Create Bundle — ${scannedItems.length} USNs`}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Button variant="outline" className="w-full" onClick={() => setStep('scanning')}>
              Back to USN Scanning
            </Button>
          </>
        )}

        {step === 'result' && result && (
          <Card className="border-green-200 bg-green-50/30" data-testid="result-card">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-green-800">Bundle Created</h2>
              <p className="text-xl font-mono font-bold text-cyan-700 mt-1">{result.bundle_code}</p>
              <p className="text-sm text-gray-500 mt-1">
                {result.total_usns} USNs • {result.design_name} {result.shade}
              </p>
              {(result.pjo_split || []).length > 0 && (
                <p className="text-xs text-slate-700 mt-1" data-testid="result-pjo-split">
                  Job orders in this bundle: <span className="font-mono">{pjoSplitText(result.pjo_split)}</span>
                </p>
              )}

              {/* Staff breakdown */}
              {(result.staff_breakdown || []).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 font-medium mb-2">Staff breakdown</p>
                  <div
                    className={`grid gap-2 ${
                      result.staff_breakdown.length === 1 ? 'grid-cols-1' :
                      result.staff_breakdown.length === 2 ? 'grid-cols-2' :
                      result.staff_breakdown.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
                    }`}
                  >
                    {result.staff_breakdown.map((s, i) => (
                      <div
                        key={s.staff_id}
                        className={`rounded-lg p-2 text-center ${
                          ['bg-blue-50', 'bg-purple-50', 'bg-amber-50', 'bg-emerald-50', 'bg-rose-50'][i % 5]
                        }`}
                      >
                        <p className="text-xs text-gray-500 truncate">{s.staff_name}</p>
                        <p className="font-bold text-gray-800">{s.attributed_qty} pcs</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-2">
                <Button className="w-full" onClick={handleReset} data-testid="new-session-btn">
                  Pack Another Bundle
                </Button>
                {isAdmin && (
                  <Button
                    variant="outline"
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => setSwapOpen(true)}
                    data-testid="admin-swap-bundle-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Change Bundle Code (Admin)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* Admin History tab */}
        {isAdmin && activeTab === 'history' && (
          <div className="space-y-3">
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-base font-semibold">Packing History</h2>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                    Admin Mode — Edit enabled
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { setHistoryPage(0); fetchHistory(); }
                    }}
                    placeholder="Search by bundle code, session ID, staff…"
                    data-testid="history-search-input"
                  />
                  <Button onClick={() => { setHistoryPage(0); fetchHistory(); }} data-testid="history-search-btn">
                    Search
                  </Button>
                </div>
                {historyLoading ? (
                  <p className="text-center text-gray-400 py-6 text-sm">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">No packing sessions found</p>
                ) : (
                  <div className="space-y-2">
                    {history.map((row) => (
                      <div
                        key={row.session_id}
                        className="border rounded-lg p-3 flex items-center gap-3"
                        data-testid={`history-row-${row.session_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-sm" data-testid={`history-bundle-${row.session_id}`}>
                              {row.bundle_code || '—'}
                            </span>
                            {row.renamed_from && (
                              <Badge
                                className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] h-5 px-1.5"
                                title={`Renamed from ${row.renamed_from} on ${row.renamed_at} by ${row.renamed_by}`}
                              >
                                RENAMED
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {row.staff_name || '—'} · {row.total_scanned ?? 0} USNs ·{' '}
                            {row.started_at ? new Date(row.started_at).toLocaleString() : '—'}
                          </p>
                          <p className="text-[11px] text-gray-400 font-mono truncate">{row.session_id}</p>
                        </div>
                        <Button
                          variant="outline" size="sm"
                          className="h-8 border-amber-400 text-amber-800 hover:bg-amber-50"
                          onClick={() => openHistoryEdit(row)}
                          disabled={!row.bundle_code}
                          data-testid={`edit-bundle-btn-${row.session_id}`}
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1" /> Change Bundle Code
                        </Button>
                      </div>
                    ))}
                    {historyTotal > 20 && (
                      <div className="flex items-center justify-between pt-2 text-sm">
                        <span className="text-gray-500">
                          Page {historyPage + 1} of {Math.ceil(historyTotal / 20)}
                        </span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={historyPage === 0}
                                  onClick={() => setHistoryPage(p => Math.max(0, p - 1))}>Prev</Button>
                          <Button variant="outline" size="sm"
                                  disabled={(historyPage + 1) * 20 >= historyTotal}
                                  onClick={() => setHistoryPage(p => p + 1)}>Next</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin: history-row edit dialog (reuses /admin/swap-bundle-code) */}
        <Dialog open={!!historyEditRow} onOpenChange={(o) => { if (!o) setHistoryEditRow(null); }}>
          <DialogContent data-testid="history-edit-dialog">
            <DialogHeader>
              <DialogTitle>Change Bundle Code (Admin)</DialogTitle>
            </DialogHeader>
            {historyEditRow && (
              <div className="space-y-3 py-2 text-sm">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                  Old code <span className="font-mono font-bold">{historyEditRow.bundle_code}</span> will be
                  <b> permanently retired</b>. Cascades to packed bundle, packing session, assorted USNs,
                  and ship boxes. The new code must already be issued via Print Codes.
                </div>
                <div className="bg-gray-50 border rounded p-3 text-xs">
                  <p>Session: <span className="font-mono">{historyEditRow.session_id}</span></p>
                  <p>Staff: {historyEditRow.staff_name || '—'} · USNs: {historyEditRow.total_scanned ?? 0}</p>
                </div>
                <div>
                  <Label>New Bundle Code</Label>
                  <Input
                    value={historyNewCode}
                    onChange={(e) => setHistoryNewCode(e.target.value.toUpperCase())}
                    placeholder="BUN000042/26-27"
                    className="font-mono"
                    data-testid="history-new-code-input"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Input
                    value={historyReason}
                    onChange={(e) => setHistoryReason(e.target.value)}
                    placeholder="e.g. wrong code scanned at packing"
                    data-testid="history-reason-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryEditRow(null)}>Cancel</Button>
              <Button
                onClick={submitHistoryEdit}
                disabled={historyEditLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="history-edit-confirm-btn"
              >
                {historyEditLoading ? 'Updating…' : 'Apply Bundle Code Change'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin: swap bundle code dialog */}
        <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
          <DialogContent data-testid="swap-bundle-dialog">
            <DialogHeader>
              <DialogTitle>Change Bundle Code (Admin)</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                <p className="font-medium">Warning</p>
                <p className="text-xs mt-1">
                  Swapping will <strong>permanently retire</strong> the old code
                  <span className="font-mono mx-1">{result?.bundle_code}</span>
                  — it cannot be reused again. The new code must have been issued via Print Codes.
                </p>
              </div>
              <div>
                <Label>New Bundle Code</Label>
                <Input
                  value={swapNewCode}
                  onChange={(e) => setSwapNewCode(e.target.value.toUpperCase())}
                  placeholder="BUN000042/26-27"
                  className="font-mono"
                  data-testid="swap-new-code-input"
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  value={swapReason}
                  onChange={(e) => setSwapReason(e.target.value)}
                  placeholder="Why is the swap needed?"
                  data-testid="swap-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSwapOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSwapBundleCode}
                disabled={swapLoading || !swapNewCode.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="swap-confirm-btn"
              >
                {swapLoading ? 'Swapping…' : 'Swap & Retire Old'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AssortmentPacking;
